import { Copy, Eye } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import type { MapaCargo } from '@/components/Configuracao/mapaCargos/mapaCargosTypes'
import {
  countMapaCargoAtividades,
  countMapaCargoSecoes,
  getMapaCargoSetorColors,
  groupMapaCargosBySetor,
} from '@/components/Configuracao/mapaCargos/mapaCargosListUtils'
import { cn } from '@/utils/cn'

const TABLE_HEADERS = ['Cargo', 'Setor', 'Blocos', 'Atividades', 'Ações'] as const

const thClass =
  'px-4 py-3 font-medium uppercase tracking-wider text-[var(--font-color-label)]'

type Props = {
  cargos: MapaCargo[]
  onView: (cargo: MapaCargo) => void
  onCopy: (cargo: MapaCargo) => void
}

export function MapaCargosList({ cargos, onView, onCopy }: Props) {
  if (cargos.length === 0) {
    return (
      <div
        className='rounded-xl border border-[var(--glass-border)] bg-[var(--glass-bg)] py-16 text-center text-[13px] text-[var(--text-muted)]'
      >
        Nenhum cargo cadastrado no mapa.
      </div>
    )
  }

  return (
    <>
      {Object.entries(groupMapaCargosBySetor(cargos)).map(([setor, setorCargos]) => {
        const colors = getMapaCargoSetorColors(setor)

        return (
          <div key={setor} className='space-y-3'>
            <div
              className='flex items-center gap-3 rounded-lg border-l-4 px-4 py-2'
              style={{ background: colors.bg, borderColor: colors.border }}
            >
              <h4
                className='m-0 font-bold uppercase tracking-wider'
                style={{ fontSize: '11px', color: colors.text }}
              >
                {setor}
              </h4>
              <Badge variant='muted' className='px-2 py-0.5 text-[10px]'>
                {setorCargos.length} {setorCargos.length === 1 ? 'cargo' : 'cargos'}
              </Badge>
            </div>

            <div className='overflow-x-auto rounded-xl border border-[var(--glass-border)]'>
              <table className='w-full'>
                <thead>
                  <tr className='border-b border-[var(--glass-border)] bg-[var(--glass-bg)]'>
                    {TABLE_HEADERS.map((header) => (
                      <th
                        key={header}
                        className={cn(
                          thClass,
                          header === 'Ações' ? 'w-28 text-right' : 'text-left',
                        )}
                        style={{ fontSize: 'var(--font-size-label)' }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {setorCargos.map((cargo) => (
                    <tr
                      key={cargo.id}
                      className='border-b border-[var(--glass-border)] transition-colors last:border-0 hover:bg-[var(--glass-bg)]'
                    >
                      <td className='px-4 py-3'>
                        <div className='flex flex-col'>
                          <span className='text-[13px] font-semibold text-[var(--text-primary)]'>
                            {cargo.titulo}
                          </span>
                          <span className='mt-0.5 line-clamp-1 text-[11px] text-[var(--text-muted)]'>
                            {cargo.secoes[0]?.titulo ?? 'Sem blocos cadastrados'}
                          </span>
                        </div>
                      </td>
                      <td className='px-4 py-3'>
                        <span className='text-[13px] text-[var(--text-muted)]'>{cargo.setor}</span>
                      </td>
                      <td className='px-4 py-3'>
                        <Badge variant='info'>{countMapaCargoSecoes(cargo)} blocos</Badge>
                      </td>
                      <td className='px-4 py-3'>
                        <Badge variant='warning'>{countMapaCargoAtividades(cargo)} itens</Badge>
                      </td>
                      <td className='px-4 py-3'>
                        <div className='flex items-center justify-end gap-0.5'>
                          <button
                            type='button'
                            onClick={() => onView(cargo)}
                            title='Ver mapa do cargo'
                            aria-label='Ver mapa do cargo'
                            className='flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-[var(--text-muted)] transition-colors hover:bg-[var(--glass-bg)] hover:text-[var(--brand-primary)]'
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            type='button'
                            onClick={() => onCopy(cargo)}
                            title='Copiar descrição'
                            aria-label='Copiar descrição'
                            className='flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-[var(--text-muted)] transition-colors hover:bg-[var(--glass-bg)] hover:text-[var(--brand-primary)]'
                          >
                            <Copy size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      })}
    </>
  )
}
